import { asyncWithLDProvider } from 'launchdarkly-react-client-sdk';
import { getOrgIdAndPropertyIdFromURL } from 'utils';
import { getLocalAuth } from 'utils/auth';
import { addFeatureFlagEvaluation } from 'utils/datadog';
import { IS_DEVELOPMENT, IS_EPHEMERAL } from '../constants/environment';

const LD_BOOTSTRAP_STORAGE_PREFIX = 'portal:ld:bootstrap';

const getBootstrapStorageKey = ({
  clientSideID,
  userId,
  organizationId,
  propertyId,
}) =>
  `${LD_BOOTSTRAP_STORAGE_PREFIX}:${clientSideID}:${userId}:${organizationId}:${propertyId}`;

const parseJson = (value) => {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

const decodeBase64UrlJson = (encoded) => {
  try {
    const normalized = encoded.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized + '='.repeat((4 - (normalized.length % 4)) % 4);
    return JSON.parse(window.atob(padded));
  } catch {
    return null;
  }
};

/**
 * Finds the previously-persisted LaunchDarkly identify context from localStorage.
 * UserContext calls ldClient.identify() with { key: authUser.id, organizationId, propertyId, propertyType }
 * after the app context loads. This context gets cached by the LD SDK.
 * At init time we don't have propertyType yet, so we decode it from the cached key.
 * Without this, bootstrap: 'localStorage' always misses because the context key won't match.
 */
const getCachedLDIdentifyContext = (
  clientSideID,
  { userId, organizationId, propertyId },
) => {
  if (!clientSideID || !userId) return null;

  const prefix = `ld:${clientSideID}:`;
  const ldKeys = Object.keys(window.localStorage)
    .filter((k) => k.startsWith(prefix))
    .filter((k) => !k.includes(':$'));

  for (const key of ldKeys) {
    const encoded = key.slice(prefix.length);
    const ctx = decodeBase64UrlJson(encoded);
    if (
      ctx?.kind === 'user' &&
      ctx.key === userId &&
      ctx.organizationId === organizationId &&
      ctx.propertyId === propertyId
    ) {
      return ctx;
    }
  }

  return null;
};

export const getStoredLaunchDarklySnapshot = ({
  clientSideID,
  userId,
  organizationId,
  propertyId,
}) => {
  if (!clientSideID || !userId || !organizationId || !propertyId) return null;

  const key = getBootstrapStorageKey({
    clientSideID,
    userId,
    organizationId,
    propertyId,
  });

  return parseJson(window.localStorage.getItem(key));
};

export const persistLaunchDarklySnapshot = ({
  clientSideID,
  context,
  flags,
}) => {
  const userId = context?.key;
  const organizationId = context?.organizationId;
  const propertyId = context?.propertyId;

  if (!clientSideID || !userId || !organizationId || !propertyId || !flags) {
    return;
  }

  const key = getBootstrapStorageKey({
    clientSideID,
    userId,
    organizationId,
    propertyId,
  });

  window.localStorage.setItem(
    key,
    JSON.stringify({
      context,
      flags,
      updatedAt: Date.now(),
    }),
  );
};

export const createLaunchDarklyProvider = async () => {
  const { propertyId, organizationId } = getOrgIdAndPropertyIdFromURL();
  const { authUser } = getLocalAuth();

  const inspectors = [
    {
      type: 'flag-used',
      name: 'dd-inspector',
      method: (key, detail) => {
        addFeatureFlagEvaluation(key, detail.value);
      },
    },
  ];

  const clientSideID = `${process.env.REACT_APP_LAUNCHDARKLY_TOKEN}`;
  const storedSnapshot = getStoredLaunchDarklySnapshot({
    clientSideID,
    userId: authUser?.id,
    organizationId,
    propertyId,
  });

  // UserContext calls ldClient.identify() after AppContext loads propertyType from the API.
  // That identify context is what gets cached in localStorage. We recover it here so that
  // bootstrap: 'localStorage' finds the right key and flags are available immediately.
  const cachedIdentifyContext =
    storedSnapshot?.context ||
    getCachedLDIdentifyContext(clientSideID, {
      userId: authUser?.id,
      organizationId,
      propertyId,
    });

  // Fall back to init context (no propertyType) if no cached identify context exists yet
  // (e.g. first ever load). UserContext.identify() will update it after AppContext loads.
  const ldContext = cachedIdentifyContext || {
    kind: 'user',
    key: authUser?.id || 'portalUser',
    organizationId,
    propertyId,
  };

  const bootstrap =
    storedSnapshot?.flags && Object.keys(storedSnapshot.flags).length
      ? storedSnapshot.flags
      : 'localStorage';

  if (IS_DEVELOPMENT || IS_EPHEMERAL) {
    console.log('Loading LaunchDarkly with bootstrap:', bootstrap);
  }

  return asyncWithLDProvider({
    clientSideID,
    context: ldContext,
    options: {
      inspectors,
      bootstrap,
    },
  });
};
